import { styled } from '@mui/material/styles';
import { Theme, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

export type StatusVariant = 'incomplete' | 'complete' | 'draft';

const Container = styled('button')<Omit<VariantObject, 'label'>>(
    ({ borderColor, backgroundColor, theme, width, height, fontSize, fontWeight, padding }: any) => ({
        borderRadius: '100px',
        border: '1px',
        borderStyle: 'solid',
        borderColor,
        backgroundColor,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width,
        color: theme.palette.neutral[950],
        height,
        ...theme.typography.labels.smallDemibold,
        ...(fontSize ? { fontSize } : {}),
        ...(fontWeight ? { fontWeight } : {}),
        ...(padding ? { padding } : {}),
        lineHeight: 'unset'
    })
);

type Props = {
    variant: StatusVariant;
};

export const Status = ({ variant }: Props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const variants = getStatusVariants(theme, t);
    const { label, ...rest } = variants.get(variant) || {};

    if (!label) {
        return null;
    }

    return <Container {...rest}>{label}</Container>;
};

export const ReviewStatus = ({ variant }: Props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const variants = getReviewStatusVariants(theme, t);
    const { label, ...rest } = variants.get(variant) || {};

    if (!label) {
        return null;
    }

    return <Container {...rest}>{label}</Container>;
};

type VariantObject = {
    borderColor?: string;
    backgroundColor?: string;
    label?: string;
    width?: number;
    height?: number;
};

export const getStatusVariants = (theme: Theme, t: any) =>
    new Map<StatusVariant, VariantObject>([
        [
            'incomplete',
            {
                borderColor: theme.palette.semantic.bd.warning,
                backgroundColor: theme.palette.semantic.bg.warning2,
                label: t('INCOMPLETE_TEXT'),
                width: 80,
                height: 32
            }
        ],
        [
            'complete',
            {
                borderColor: theme.palette.semantic.bd.success,
                backgroundColor: theme.palette.semantic.bg.success1,
                label: t('COMPLETE_TEXT'),
                width: 80,
                height: 32
            }
        ],
        [
            'draft',
            {
                borderColor: theme.palette.neutral[400],
                backgroundColor: theme.palette.neutral[50],
                label: t('INCOMPLETE_DRAFT_TEXT'),
                width: 80,
                height: 32
            }
        ]
    ]);

export const getReviewStatusVariants = (theme: Theme, t: TFunction) =>
    new Map([
        [
            'incomplete',
            {
                borderColor: theme.palette.semantic.int.disabled2,
                backgroundColor: theme.palette.semantic.int.disabled1,
                label: t('INCOMPLETE_TEXT'),
                height: 32,
                fontSize: '16px',
                fontWeight: 500,
                padding: '12px'
            }
        ],
        [
            'complete',
            {
                borderColor: theme.palette.semantic.int.disabled2,
                backgroundColor: theme.palette.semantic.int.disabled1,
                label: t('COMPLETE_TEXT'),
                height: 32,
                fontSize: '16px',
                fontWeight: 500,
                padding: '12px'
            }
        ],
        [
            'draft',
            {
                borderColor: theme.palette.semantic.int.disabled2,
                backgroundColor: theme.palette.semantic.int.disabled1,
                label: t('INCOMPLETE_DRAFT_TEXT'),
                height: 32,
                fontSize: '16px',
                fontWeight: 500,
                padding: '12px'
            }
        ]
    ]);
